/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { MediaType } from '../constants/media-type'
import { API_URL } from '../constants/urls'
import {
  type DownloadFileResponse,
  ErrorResponseSchema,
  type FailedResponse,
  type SuccessResponseWithoutPayload
} from './types'
import moment from 'moment'

/* Constants */

const GENERIC_NETWORK_ERROR = Error('Network Error')

export const UnknownNetworkErrorCode = -1
export const UnauthorizedErrorCode = 401
export const ForbiddenErrorCode = 403 // Error code when accessing API with insufficient privileges
export const BadRequestErrorCode = 400
export const NotFoundErrorCode = 404
export const UnprocessableEntity = 422

/* Exported 'API' */

/**
 * Api Utilities.
 */
export const ApiUtil = {
  doDeleteRequest,
  downloadCsvFile,
  doGetRequest,
  doPatchRequest,
  doPostRequest,
  doPutRequest,
  errorResponse,
  execDeleteRequest,
  failedResponseToDisplayString,
  formatEpochSecondsToDateString,
  getApiUrl,
  postDownloadFileRequest: postDownloadCsvFileRequest,
  unknownExceptionResponse,
  shouldForceLogout
}

/* Exported Functions */

async function execDeleteRequest(
  url: string,
  token: string,
  apiDesc: string
): Promise<SuccessResponseWithoutPayload | FailedResponse> {
  try {
    const response = await ApiUtil.doDeleteRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    return {
      succeeded: true
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Execute 'DELETE' request.
 * @param apiUrl full API url.
 * @param token authentication token.
 */
async function doDeleteRequest(apiUrl: string, token: string): Promise<Response> {
  const requestOptions = createDeleteRequestOpts(token)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute a 'GET' request.
 * @param apiUrl full API url.
 * @param token authentication token.
 * @param acceptedMediaType Optional: Accepted Media Type (specify, if not 'application/json').
 */
async function doGetRequest(
  apiUrl: string,
  token: string,
  acceptedMediaType: string = MediaType.JSON
): Promise<Response> {
  const requestOptions = createGetRequestOpts(token, acceptedMediaType)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute a 'PATCH' request.
 * @param apiUrl full API url.
 * @param body request body.
 * @param token authentication token.
 */
async function doPatchRequest(apiUrl: string, body: Record<string, unknown>, token: string): Promise<Response> {
  const requestOptions = createPatchRequestOpts(body, token)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute a 'POST' request.
 * @param apiUrl full API url.
 * @param body request body.
 * @param token authentication token.
 * @param acceptedMediaType Optional: Accepted Media Type (specify, if not 'application/json').
 */
async function doPostRequest<T extends Record<string, unknown>>(
  apiUrl: string,
  body: T | FormData,
  token: string,
  acceptedMediaType: string = MediaType.JSON
): Promise<Response> {
  const requestOptions = createPostRequestOpts(body, token, acceptedMediaType)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute a 'PUT' request.
 * @param apiUrl full API url.
 * @param body request body.
 * @param token authentication token.
 */
async function doPutRequest(apiUrl: string, body: Record<string, unknown>, token: string): Promise<Response> {
  const requestOptions = createPutRequestOpts(body, token)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Get a full API url for the API path.
 * @param apiPath
 */
function getApiUrl(apiPath: string): string {
  return `${API_URL}${apiPath}`
}

/**
 * Download CSV file.
 */
async function downloadCsvFile(input: { url: string, token: string, apiDesc: string }): Promise<DownloadFileResponse> {
  try {
    const response = await ApiUtil.doGetRequest(input.url, input.token, MediaType.CSV)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, input.apiDesc)
    }
    const payload: string = await response.text()
    return {
      succeeded: true,
      payload: {
        content: payload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, input.url, input.apiDesc)
  }
}

async function postDownloadCsvFileRequest(
  input: { url: string, token: string, apiDesc: string, requestBody: Record<string, string> }
): Promise<DownloadFileResponse> {
  try {
    const response = await ApiUtil.doPostRequest(input.url, input.requestBody, input.token, MediaType.CSV)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, input.apiDesc)
    }

    const payload: string = await response.text()

    return {
      succeeded: true,
      payload: {
        content: payload
      }
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, input.url, input.apiDesc)
  }
}

/**
 * Produce API call failure response.
 * @param response Fetch-API response object.
 * @param apiDesc API description (for console logging purposes).
 */
async function errorResponse(response: Response, apiDesc: string): Promise<FailedResponse> {
  const { status: statusCode } = response

  console.warn(`Error response for ${apiDesc}: `, statusCode)
  let key: string | undefined
  let message: string | undefined
  if (statusCode === BadRequestErrorCode || statusCode === NotFoundErrorCode || statusCode === UnprocessableEntity) {
    // find key + message
    const details = await tryGetErrorDetails(response)
    key = details?.errorKey
    message = details?.message
  }

  return {
    succeeded: false,
    errorKey: key,
    errStatusCode: statusCode,
    message
  }
}

/**
 * Produce API call failure response for the case when an exception is thrown.
 * @param ex thrown error/exception.
 * @param url full API url.
 * @param apiDesc API description (for console logging purposes).
 * @param body Optional: request body.
 */
function unknownExceptionResponse(ex: unknown, url: string, apiDesc: string, body: Record<string, unknown> = {}): FailedResponse {
  if (!body) {
    console.warn(`Error occurred on execution of ${apiDesc}: `, ex, ` (apiUrl: ${url})`)
  } else {
    console.warn(`Error occurred on execution of ${apiDesc}: `, ex, ` (apiUrl: ${url}, body: ${JSON.stringify(body)})`)
  }

  return {
    succeeded: false,
    errorKey: undefined,
    errStatusCode: UnknownNetworkErrorCode
  }
}

/**
 * Check if the response indicates that a 'force logout' should be initiated.
 * @param res error response.
 */
function shouldForceLogout(res: FailedResponse): boolean {
  switch (res.errStatusCode) {
    case UnauthorizedErrorCode:
    case ForbiddenErrorCode:
      return true
    default:
      return false
  }
}

/**
 * Create a message from a failed response (error). Should be (currently) u
 * @param response failed response output from an API function.
 */
function failedResponseToDisplayString(response: FailedResponse): string {
  return `${response.message} (errorKey: '${response.errorKey}', statusCode: '${response.errStatusCode}')`
}

/* Helper Functions */

function createDeleteRequestOpts(token: string): RequestInit {
  return {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON
    }
  }
}

function createGetRequestOpts(token: string, mediaType: string): RequestInit {
  return {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: mediaType
    }
  }
}

function createPatchRequestOpts(body: Record<string, unknown>, token: string): RequestInit {
  return {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON,
      'Content-Type': MediaType.JSON
    },
    body: JSON.stringify(body)
  }
}

function createPostRequestOpts(
  body: Record<string, unknown> | FormData,
  token: string,
  acceptedMediaType: string): RequestInit {
  const headers: HeadersInit = (body instanceof FormData)
    ? {
        Authorization: `Bearer ${token}`,
        Accept: acceptedMediaType
      }
    : {
        Authorization: `Bearer ${token}`,
        Accept: acceptedMediaType,
        'Content-Type': MediaType.JSON
      }

  return {
    method: 'POST',
    headers,
    body: body instanceof FormData ? body : JSON.stringify(body)
  }
}

function createPutRequestOpts(body: Record<string, unknown>, token: string): RequestInit {
  return {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON,
      'Content-Type': MediaType.JSON
    },
    body: JSON.stringify(body)
  }
}

async function handleRequest(networkCallFunc: () => Promise<Response>): Promise<Response> {
  try {
    return await networkCallFunc()
  } catch (e) {
    console.error('Network error occurred:', e)
    throw GENERIC_NETWORK_ERROR
  }
}

async function tryGetErrorDetails(errorRes: Response): Promise<ErrorDetails | undefined> {
  const payload: unknown = await errorRes.json()
  if (payload && typeof payload === 'object') {
    const errResponse = ErrorResponseSchema.safeParse(payload)
    if (errResponse.success) {
      return {
        errorKey: errResponse.data.key,
        message: errResponse.data.message
      }
    }
  }

  return undefined
}

function formatEpochSecondsToDateString(epochSeconds: number): string {
  return moment(epochSeconds).format('YYYY-MM-DD')
}

interface ErrorDetails {
  errorKey: string
  message: string
}
